import * as React from 'react';
import { connect } from 'react-redux';

type Props = {
  lastSaveAt?: number | null;
  updating?: boolean;
};

type State = {
  now: number;
};

/**
 * V3AutosaveIndicator — live "Auto-saved N sec ago" in the Lite nav bar.
 *
 * Connected to `liteProject.lastSaveAt` (bumped on CREATE_LITE_PROJECT_SUCCESS
 * and UPDATE_LITE_PROJECT_SUCCESS) and `liteProject.updating` (true while a
 * PUT is in flight). A 1-second interval re-renders so the relative time
 * stays current without requiring a Redux dispatch on each tick.
 *
 *   in-flight PUT       → "Saving…"
 *   lastSaveAt present  → "Auto-saved just now" / "Auto-saved 12s ago" / "Auto-saved 3m ago"
 *   neither             → "Not saved yet"
 */
class V3AutosaveIndicator extends React.Component<Props, State> {
  state: State = { now: Date.now() };
  private interval: any = null;

  componentDidMount() {
    this.interval = setInterval(this.tick, 1000);
  }

  componentWillUnmount() {
    if (this.interval) clearInterval(this.interval);
  }

  tick = () => this.setState({ now: Date.now() });

  render() {
    const { lastSaveAt, updating } = this.props;
    const { now } = this.state;

    let label: string;
    if (updating) {
      label = 'Saving…';
    } else if (lastSaveAt) {
      const secondsAgo = Math.max(0, Math.floor((now - lastSaveAt) / 1000));
      if (secondsAgo < 5) {
        label = 'Auto-saved just now';
      } else if (secondsAgo < 60) {
        label = `Auto-saved ${secondsAgo}s ago`;
      } else if (secondsAgo < 3600) {
        const mins = Math.floor(secondsAgo / 60);
        label = `Auto-saved ${mins}m ago`;
      } else {
        const hrs = Math.floor(secondsAgo / 3600);
        label = `Auto-saved ${hrs}h ago`;
      }
    } else {
      label = 'Not saved yet';
    }

    return (
      <span className="v3-autosave">
        <span className="v3-autosave-dot" />
        {label}
      </span>
    );
  }
}

function mapStateToProps(appState: any) {
  const liteProject = appState.liteProject || {};
  return {
    lastSaveAt: liteProject.lastSaveAt,
    updating: !!liteProject.updating,
  };
}

export default connect(mapStateToProps)(V3AutosaveIndicator);
